import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import Slider from "react-slick";
import {
  deleteEventUrl,
  getEventDetailsUrl,
  interestForEventUrl,
  registerForEventUrl,
  updateEventUrl,
} from "Constant/api";
import { paths } from "Constant/paths";
import { newAuthService } from "Service/Axios";
import {
  compareDates,
  formatDateWithShortMonth,
  getFirstLetterOfName,
} from "Utils";
import { LoadingComponent } from "Components";

const settings = {
  infinite: false,
  dots: false,
  arrows: true,
  variableWidth: true,
  focusOnSelect: true,
  speed: 500,
  slidesToShow: 1,
  slidesToScroll: 1,
  responsive: [
    {
      breakpoint: 1300,
      settings: {
        slidesToShow: 1,
        slidesToScroll: 1,
        infinite: false,
        arrows: true,
      },
    },
  ],
};

export const PreviewEvent = () => {
  const { t } = useTranslation();
  const { gid } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [eventDetails, setEventDetails] = useState(null);

  const getEventDetails = async () => {
    setLoading(true);
    const payload = {
      eventId: gid,
    };
    const response = await newAuthService(getEventDetailsUrl, "POST", payload);
    if (response.status) {
      setEventDetails(response.data);
      setLoading(false);
    } else {
      setEventDetails(null);
      setLoading(false);
    }
  };

  useEffect(() => {
    getEventDetails();
  }, []);

  // Handle
  const handlePublish = async () => {
    try {
      const payload = {
        status: "publish",
        eventId: gid,
      };
      const response = await newAuthService(updateEventUrl, "POST", payload);
      if (response.status) {
        toast.success(t("common.messages.notification.success.publishEvent"));
        navigate(paths.admin.events.root);
      } else {
        toast.error(response.message);
      }
    } catch (error) {
      toast.error(t("common.messages.notification.failed.somethingWentWrong"));
    }
  };

  const handleRegisterEvent = async (
    registrationFormRequired,
    registrationForm
  ) => {
    if (registrationFormRequired) {
      navigate(`${paths.formInstruction}/${registrationForm}`);
      return;
    } else {
      try {
        const payload = {
          eventId: gid,
        };
        const response = await newAuthService(
          registerForEventUrl,
          "POST",
          payload
        );
        if (response.status) {
          toast.success(
            t("common.messages.notification.success.registerEvent")
          );
        } else {
          toast.error(response.message);
        }
      } catch (error) {
        toast.error(
          t("common.messages.notification.failed.somethingWentWrong")
        );
      }
    }
  };

  const handleInterestEvent = async () => {
    try {
      const payload = {
        eventId: gid,
      };
      const response = await newAuthService(
        interestForEventUrl,
        "POST",
        payload
      );
      if (response.status) {
        toast.success(t("common.messages.notification.success.interestEvent"));
      } else {
        toast.error(response.message);
      }
    } catch (error) {
      toast.error(t("common.messages.notification.failed.somethingWentWrong"));
    }
  };

  const handleDeleteEvent = async () => {
    try {
      setLoading(true);
      const payload = {
        eventId: gid,
      };
      const response = await newAuthService(deleteEventUrl, "POST", payload);
      if (response.status) {
        toast.success(t("common.messages.notification.success.deleteEvent"));
        navigate(paths.admin.events.root);
      } else {
        toast.error(response.message);
      }
    } catch (error) {
      toast.error(t("common.messages.notification.failed.somethingWentWrong"));
    } finally {
      setLoading(false);
    }
  };

  const handleGoBack = () => {
    navigate(`${paths.admin.events.create}?eventId=${gid}`);
  };

  return (
    <LoadingComponent isLoading={loading}>
      <main className="main">
        {eventDetails && (
          <div className="mb-4">
            <div className="p-3 rounded-3 bg-white position-relative">
              <div className="d-flex flex-wrap align-items-center row-gap-3 mb-3">
                <div className="">
                  <h3 className="text-md font-semibold mb-0">Event Preview</h3>
                </div>
                <div className="ms-md-auto">
                  <Link
                    to={paths.admin.events.root}
                    className="btn btn-transparent border-0"
                  >
                    <i className="bx bx-x bx-sm"></i>
                  </Link>
                </div>
              </div>

              <div className="">
                <div className="rounded-3 overflow-hidden position-relative pre-event-banner mb-4">
                  <img
                    src={
                      eventDetails.banner
                        ? eventDetails.banner
                        : "/assets/images/placeholders/event-placeholder.svg"
                    }
                    alt={eventDetails.eventName}
                    className="w-100 h-100 d-block object-fit-cover"
                  />
                  <div className="p-3 position-absolute start-0 bottom-0 end-0">
                    <div className="d-grid gap-2 d-md-flex">
                      <button className="btn btn-light">
                        {eventDetails.registrationFormRequired
                          ? "Registration Required"
                          : "Open for all"}
                      </button>
                    </div>
                  </div>
                </div>

                <div className="d-flex justify-content-end align-items-center gap-2 mb-3">
                  {eventDetails?.inviteLink?.trim() && (
                    <a
                      href={eventDetails?.inviteLink}
                      className="btn btn-outline-primary"
                    >
                      Join Now
                    </a>
                  )}
                  {eventDetails?.registerEventShow && (
                    <button
                      className="btn btn-primary"
                      onClick={() =>
                        handleRegisterEvent(
                          eventDetails?.registrationFormRequired,
                          eventDetails?.registrationForm
                        )
                      }
                    >
                      Register Event
                    </button>
                  )}
                </div>

                <div className="mb-3">
                  <h3 className="h5 font-semibold">{eventDetails.eventName}</h3>
                </div>
                <div className="mb-5">
                  <ul className="m-0 mb-3 p-0 list-unstyled text-sm font-medium text-slate-600 d-flex flex-column row-gap-2">
                    <li className="d-flex align-items-center column-gap-1">
                      <i className="bx bx-calendar bx-sm text-slate-500"></i>
                      {eventDetails.date &&
                        formatDateWithShortMonth(eventDetails.date)}{" "}
                      | {eventDetails.time}
                    </li>
                    <li className="d-flex align-items-center column-gap-1">
                      <i className="bx bx-map bx-sm text-slate-500"></i>
                      {eventDetails.locationType}
                    </li>
                  </ul>

                  {eventDetails?.registeredAttendeeDetails?.length > 0 && (
                    <div className="d-flex flex-rap align-items-center">
                      <div className="header_img me-2">
                        {eventDetails.registeredAttendeeDetails
                          .slice(0, 3)
                          .map((attendee, index) => (
                            <div
                              className="img-box overflow-hidden rounded-circle bg-white"
                              key={index.toString()}
                            >
                              <img
                                src={attendee.photoURL}
                                alt=""
                                className="w-100 h-100 object-fit-cover"
                              />
                            </div>
                          ))}
                        {eventDetails?.registeredAttendeeDetails?.length >
                          3 && (
                          <span className="more-img">
                            +{eventDetails.registeredAttendeeDetails.length - 3}
                          </span>
                        )}
                      </div>
                      <span className="text-sm text-slate-500">
                        <strong>
                          {eventDetails?.registeredAttendeeDetails?.length}
                        </strong>{" "}
                        registered attendees
                      </span>
                    </div>
                  )}
                </div>

                {eventDetails?.latestUpdateShow && (
                  <div className="mb-5">
                    <div className="p-4 rounded-3 border border-orange-300 bg-orange-50 d-flex align-items-center gap-3">
                      <div className="text-sm">
                        <h4 className="text-md font-medium ">
                          Want the Latest Updates from this Event?
                        </h4>
                        <p>{eventDetails?.latestUpdateText}</p>
                      </div>
                      <div className="ms-auto">
                        <button
                          type="button"
                          className="btn btn-outline-green ms-auto text-nowrap"
                          onClick={handleInterestEvent}
                        >
                          <span className="material-symbols-outlined align-middle me-1">
                            thumb_up
                          </span>
                          Interested
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                <div className="mb-3">
                  <div className="slide-status-nav position-relative bg-white position-sticky top-0 z-1 mb-3">
                    <Slider {...settings}>
                      <div className="nav-status-item">
                        <Link>Details</Link>
                      </div>
                      <div className="nav-status-item">
                        <Link>Terms & Condition</Link>
                      </div>
                    </Slider>
                  </div>
                  <div className="p-4 text-sm text-slate-600 border rounded-3 mb-3">
                    <h4 className="text-md font-semibold">Details</h4>
                    <div className="mb-3">
                      <div className="d-flex align-items-center mb-3">
                        <span>
                          Organized by{" "}
                          <strong>{eventDetails.organizedBy}</strong>
                        </span>
                      </div>
                      <div
                        dangerouslySetInnerHTML={{
                          __html: eventDetails.description,
                        }}
                      />
                    </div>

                    <div>
                      <h4 className="text-base font-medium text-slate-400">
                        Guest Speakers
                      </h4>
                      <div className="d-flex flex-wrap gap-2">
                        {eventDetails.specialGuestDetails?.length > 0 &&
                          eventDetails.specialGuestDetails.map((guest, idx) => (
                            <div
                              className="p-1 rounded-pill d-flex align-items-center bg-slate-100 position-relative"
                              key={`${guest.gid}-${idx}`}
                            >
                              <div className="w-10 h-10 rounded-circle overflow-hidden flex-none me-2 border border-white">
                                {guest.photoURL ? (
                                  <img
                                    src={guest.photoURL}
                                    alt={`${guest.firstName}${guest.lastName}`}
                                    className="w-100 h-100 object-fit-cover"
                                  />
                                ) : (
                                  getFirstLetterOfName(guest)
                                )}
                              </div>
                              <span className="text-xs font-medium text-slate-500 me-3">
                                {guest.firstName} {guest.lastName}
                              </span>
                            </div>
                          ))}
                      </div>
                    </div>
                  </div>
                  <div className="p-4 text-sm text-slate-600 border rounded-3 mb-3">
                    <h3 className="text-md font-semibold">Terms & Condition</h3>
                    <p>{eventDetails.termCondition}</p>
                  </div>
                </div>
                {eventDetails?.createdEventBy && (
                  <div className="mb-5 d-flex flex-wrap gap-2 justify-content-center">
                    {compareDates(eventDetails?.date, new Date()) && (
                      <button
                        className="btn btn-primary"
                        type="button"
                        onClick={handleGoBack}
                      >
                        Go Back & Edit
                      </button>
                    )}
                    <button
                      className="btn btn-outline-danger"
                      type="button"
                      onClick={handleDeleteEvent}
                    >
                      Delete Event
                    </button>
                    <button
                      className="btn btn-primary"
                      type="button"
                      onClick={handlePublish}
                    >
                      Publish Event
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </main>
    </LoadingComponent>
  );
};
